import React, { useEffect, useState, ReactNode } from 'react'
import { BadgeVariant, Button, Flex, Input, Modal, StatusBadge, Text, TextVariant, Wallet } from '@mprolab/uikit'
import styled, { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useFormik } from 'formik'
import { BNToNumberWithDecimals, blockInvalidFloat } from '@mprolab/sdk'
import isNil from 'lodash/isNil'
import Image from 'components/CompatImage'
import { tokenLogoSrc } from 'utils/tokenLogoUri'
import { MaxButton } from 'views/Exchange/components/General'
import { displayDuration } from 'utils/displayDuration'
import { formatNumberWithSpaces } from 'utils/formatNumberWithSpaces'
import { BigNumber, ethers } from 'ethers'
import { observer } from 'mobx-react-lite'
import { StakeProgress, stakeStore } from 'mobxStores/StakeStore'
import { getErrorCodesDescription } from 'utils/getErrorCodesDescription'
import ChainScanFooter from 'components/ChainScanFooter'
import { useClassicStake, useStakeTimestampCounter } from '../classicStake.logic'
import { StakeOverviewInfo } from '../components'
import UseAnimations from 'react-useanimations'
import loading from 'react-useanimations/lib/loading'

const ContentWrapper = styled(Flex)`
  flex-direction: column;
  margin-top: 32px;
  width: 550px;
  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 100%;
  }
`

const Wrapper = styled(Flex)`
  gap: 8px;
  align-items: center;
`

const TokenWrapper = styled(Flex)`
  align-items: center;
  background: #171717;
  padding: 8px 16px 8px 8px;
  gap: 8px;
  border-radius: 45px;
`

const TableWrapper = styled(Flex)`
  padding: 16px;
  border-radius: 8px;
  justify-content: center;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.gray10};
`

const BadgeWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
`

interface UnstakeProps {
  fetchClassicStakeConfig: (userAccount?: string, refetch?: boolean) => Promise<void>
  isLoading?: boolean
  onDismiss?: () => void
}

const UnstakeModal: React.FC<UnstakeProps> = observer(({ onDismiss, isLoading, fetchClassicStakeConfig }) => {
  const { stake, staker, stakeProgress, error, txHash } = stakeStore
  const { account } = useWeb3React()
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { totalTime, counterLoading } = useStakeTimestampCounter(stake)
  const { unstake } = useClassicStake()
  const { getEnableUnstakeForReleaseValue } = useClassicStake()
  const [enableToUnstake, setEnableToUnstake] = useState(BigNumber.from(0))
  const [enableToUnstakeLoading, setEnableToUnstakeLoading] = useState(false)

  const stakeToken = (): { name: string; image: ReactNode } => {
    const tokenAImage = stake?.token0?.logoURI || stake?.stakeToken?.logoURI
    const tokenBImage = stake?.token1?.logoURI

    return {
      name: stake.stakeToken?.token || `${stake.token0?.token}-${stake.token1?.token}`,
      image: (
        <Flex width={tokenBImage ? '32px' : '16'} height="16px" position="relative">
          <Image src={tokenLogoSrc(tokenAImage)} width={16} height={16} />
          {tokenBImage && <Image src={tokenLogoSrc(tokenBImage)} width={16} height={16} />}
        </Flex>
      )
    }
  }

  const { name: stakeTokenName, image: stakeTokenImage } = stakeToken()

  const dataLoading = isLoading || counterLoading

  const formik = useFormik({
    validateOnBlur: false,
    validateOnChange: true,
    initialValues: {
      amount: 0
    },
    validationSchema: Yup.object().shape({
      amount: Yup.number()
        .required(t('stakingPanel.amountRequired'))
        /*
         * Against what this wallet actually holds, not the whole pool.
         *
         * This compared the amount to `totalStakedSupply` — every staker's
         * tokens added together. Somebody with 100 MPRO in a contract holding
         * ten million could type five million, pass validation, and be sent to
         * a transaction that could only revert. The message it would have
         * shown, "amount exceeds your staked balance", was never reachable.
         */
        .test('max', t('stakingPanel.amountExceedsStakedTokenBalance'), function maxAmountValidation(value) {
          return value <= BNToNumberWithDecimals(staker?.staked, stake.stakeToken?.decimals, 6)
        })
        /*
         * And against what is releasable right now. These stakes release in
         * periods; the amount above the current release cannot be withdrawn
         * yet however much is staked. It was already read and displayed above
         * the input — it just was not enforced.
         */
        .test('releasable', t('stakingPanel.amountExceedsReleasable'), function releasableValidation(value) {
          if (enableToUnstake.isZero()) return true
          return value <= BNToNumberWithDecimals(enableToUnstake, stake.stakeToken?.decimals, 6)
        })
        .test('min', t('stakingPanel.amountRequired'), function minAmountValidation(value) {
          return value > 0
        })
    }),
    onSubmit: async (values, { validateForm }) => {
      try {
        await validateForm(values)
        stakeStore.setStakeProgress(StakeProgress.CONFIRMING)
        await unstake(
          stake.contractAddress,
          // parseUnits defaults to 18. The stake token decides, and a stake
          // token with different decimals would have been off by orders of
          // magnitude — silently, because MPRO happens to be 18.
          ethers.utils.parseUnits(values.amount.toString(), stake.stakeToken?.decimals ?? 18)
        )
        await fetchClassicStakeConfig(account, true)
      } catch (error) {
        stakeStore.setStakeProgress(StakeProgress.ERROR)
      }
    }
  })

  useEffect(() => {
    setEnableToUnstakeLoading(true)
    getEnableUnstakeForReleaseValue(stake.contractAddress)
      .then((value) => {
        setEnableToUnstake(value)
        setEnableToUnstakeLoading(false)
      })
      .catch(() => {
        setEnableToUnstakeLoading(false)
      })
  }, [account])

  const unstakeModalContent = () => {
    switch (stakeProgress) {
      case StakeProgress.INITIAL:
        return (
          <ContentWrapper>
            <Flex fullWidth justifyContent="space-between" mb="8px">
              <Text variant={TextVariant.TEXT_XS_BOLD}>{t('stakingPanel.unstakeAmount')}</Text>
              <Flex gap="8px" alignItems="center">
                <Wallet width={16} height={16} color={colors.gray3} />
                {enableToUnstakeLoading ? (
                  <Flex minWidth="50px" justifyContent="center">
                    <UseAnimations animation={loading} size={14} strokeColor={colors.white3} />
                  </Flex>
                ) : (
                  <Text variant={TextVariant.TEXT_XS_BOLD} color={colors.gray3}>
                    {`${formatNumberWithSpaces(
                      BNToNumberWithDecimals(enableToUnstake, stake.stakeToken?.decimals)
                    )} ${stakeTokenName}`}
                  </Text>
                )}
              </Flex>
            </Flex>
            <Input
              value={formik.values.amount}
              name="amount"
              type="number"
              onKeyDown={(e) => {
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                  e.preventDefault()
                }
                blockInvalidFloat(e)
              }}
              error={!isNil(formik.errors.amount)}
              errorMsg={formik.errors.amount as string}
              onChange={(e) => {
                let { value } = e.target
                value = value.replace(',', '.')
                formik.setFieldValue('amount', value)
              }}
              endIcon={
                <Wrapper>
                  {BNToNumberWithDecimals(enableToUnstake) > 0 && (
                    <MaxButton
                      onClick={() =>
                        formik.setFieldValue(
                          'amount',
                          BNToNumberWithDecimals(enableToUnstake, stake.stakeToken?.decimals)
                        )
                      }
                    />
                  )}
                  <TokenWrapper>
                    {stakeTokenImage}
                    <Text variant={TextVariant.BODY_SMALL_BOLD}>{stakeTokenName}</Text>
                  </TokenWrapper>
                </Wrapper>
              }
            />
            <Flex mt="32px" mb="8px">
              <Text variant={TextVariant.BODY_SMALL} textTransform="uppercase" color={colors.gray6}>
                {t('stakingPanel.overview')}
              </Text>
            </Flex>
            <TableWrapper>
              <StakeOverviewInfo
                label={t(`stakingPanel.currentlyStakeAmount`)}
                value={`${formatNumberWithSpaces(
                  BNToNumberWithDecimals(staker?.staked, stake.stakeToken?.decimals, 6)
                )} ${stakeTokenName}`}
                {...{ isLoading: dataLoading }}
              />
              <StakeOverviewInfo
                label={t(`stakingPanel.totalStakeAmount`)}
                value={`${formatNumberWithSpaces(
                  BNToNumberWithDecimals(stake?.totalStakedSupply, stake.stakeToken?.decimals, 6)
                )} ${stakeTokenName}`}
                {...{ isLoading: dataLoading }}
              />
              <StakeOverviewInfo
                label={t(`stakingPanel.apy`)}
                value={`${stake?.apr}%`}
                valueColor={colors.green}
                {...{ isLoading: dataLoading }}
              />
              <StakeOverviewInfo
                label={t(`stakingPanel.stakeDuration`)}
                value={displayDuration({ time: totalTime })}
                isLast
                {...{ isLoading: dataLoading }}
              />
            </TableWrapper>
            <Flex mt="32px">
              <Button fullWidth disabled={!isNil(formik.errors.amount)} onClick={() => formik.handleSubmit()}>
                {t('stakingPanel.unstake')}
              </Button>
            </Flex>
          </ContentWrapper>
        )
      case StakeProgress.APPROVING:
        return (
          <BadgeWrapper>
            <StatusBadge title={t('modal.transactionApproving')} variant={BadgeVariant.PENDING} />
          </BadgeWrapper>
        )
      case StakeProgress.CONFIRMING:
        return (
          <BadgeWrapper>
            <StatusBadge title={t('modal.continueInWallet')} variant={BadgeVariant.PENDING} />
          </BadgeWrapper>
        )
      case StakeProgress.PENDING:
        return (
          <BadgeWrapper>
            <StatusBadge title={t('transactionPending')} variant={BadgeVariant.PENDING} />
          </BadgeWrapper>
        )
      case StakeProgress.SUCCESS:
        return (
          <Flex flexDirection="column" gap="16px">
            <BadgeWrapper>
              <StatusBadge title={t('modal.transactionSucessful')} variant={BadgeVariant.SUCCESS} />
            </BadgeWrapper>
            <ChainScanFooter chainId={stake?.chainId} transactionHash={txHash} />
          </Flex>
        )
      case StakeProgress.ERROR:
      case StakeProgress.REJECTED:
        return (
          <BadgeWrapper>
            <StatusBadge title={getErrorCodesDescription(t, error)} variant={BadgeVariant.ERROR} />
          </BadgeWrapper>
        )

      default:
        return <></>
    }
  }

  const modalTitle = () => {
    switch (stakeProgress) {
      case StakeProgress.INITIAL:
      case StakeProgress.SUCCESS:
        return `${t('stakingPanel.unstake')} ${stakeTokenName}`
      case StakeProgress.CONFIRMING:
        return t('modal.confirmTransaction')
      case StakeProgress.APPROVING:
        return t('modal.approving')
      case StakeProgress.PENDING:
        return t('modal.transactionConfirmation')
      case StakeProgress.ERROR:
        return t('modal.transactionFailed')
      case StakeProgress.REJECTED:
        return t('modal.rejected')
      default:
        return ''
    }
  }

  return (
    <Modal onDismiss={onDismiss} hideCloseButton={false} title={modalTitle()}>
      <ContentWrapper>{unstakeModalContent()}</ContentWrapper>
    </Modal>
  )
})

export default UnstakeModal
